import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Link2, Plus } from 'lucide-react';
import { Combobox } from '../components/Combobox';
import { FilterField } from '../components/FilterField';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useAbbinatoreFacets } from '../hooks/useAbbinatoreFacets';
import MachineDetailModal from '../components/MachineDetailModal';
import CreateMachineModal from '../components/CreateMachineModal';
import {
  fetchMachines, fetchMarche, fetchTipologie,
  MachineRow, MarcaOption,
} from '../api';

const PAGE_SIZE = 250;
const ROW_HEIGHT = 36; // px

export default function MacchineSection() {
  // Filters
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState<string | null>(null);
  const [tipo, setTipo] = useState<string | null>(null);
  const [unmatched, setUnmatched] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  // Data
  const [rows, setRows] = useState<MachineRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // Filtri: opzioni ristrette dalla marca selezionata.
  const { marche, tipologie } = useAbbinatoreFacets('machines', brand, {
    value: tipo, onChange: setTipo,
  });
  // Liste complete per i modali (creazione/modifica macchina): lì devi
  // poter scegliere anche una tipologia che oggi nessuno usa.
  const [tutteMarche, setTutteMarche] = useState<MarcaOption[]>([]);
  const [tutteTipologie, setTutteTipologie] = useState<{ code: string; label: string }[]>([]);

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Dialog "vedi dettaglio macchina"
  const [dialogMachine, setDialogMachine] = useState<MachineRow | null>(null);

  // Sentinel for infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMarche().then(setTutteMarche).catch(() => {});
    fetchTipologie().then(setTutteTipologie).catch(() => {});
  }, []);

  // Filter signature → reset list
  const filterSig = useMemo(
    () => JSON.stringify([debouncedSearch, brand, tipo, unmatched]),
    [debouncedSearch, brand, tipo, unmatched],
  );

  // Reset on filter change
  useEffect(() => {
    setRows([]); setPage(0); setHasMore(true);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [filterSig]);

  // Fetch page
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    fetchMachines({
      search: debouncedSearch || undefined,
      brand: brand || undefined,
      tipo: tipo || undefined,
      unmatched,
      page,
      pageSize: PAGE_SIZE,
    })
      .then(r => {
        if (cancel) return;
        setRows(prev => page === 0 ? r.items : [...prev, ...r.items]);
        setTotal(r.total);
        setHasMore((page + 1) * PAGE_SIZE < r.total && r.items.length > 0);
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [filterSig, page]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll trigger
  useInfiniteScroll(sentinelRef, () => {
    if (!loading && hasMore) setPage(p => p + 1);
  }, hasMore && !loading);

  const brandOpts = useMemo(() => marche.map(m => ({ code: m.code, label: m.label })), [marche]);
  const tipoOpts = useMemo(() => tipologie, [tipologie]);

  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
  const toggleAll = () => {
    const s = new Set(selected);
    if (allSelected) rows.forEach(r => s.delete(r.id));
    else rows.forEach(r => s.add(r.id));
    setSelected(s);
  };
  const toggleOne = (id: string) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const openDialog = (m: MachineRow) => {
    setDialogMachine(m);
  };

  const refreshList = () => {
    setRows([]); setPage(0); setHasMore(true);
  };

  // Modal creazione nuova macchina
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="flex flex-col h-full" data-testid="macchine-section">
      {/* Filtri orizzontali compatti (label a lato) */}
      <div className="flex items-center gap-4 flex-wrap bg-white border border-[#e2e2df] rounded-[10px] px-3.5 py-2.5 mb-3.5 flex-none">
        <FilterField label="Marca" className="w-[300px] flex-none">
          <Combobox
            value={brand}
            onChange={setBrand}
            options={brandOpts}
            placeholder="Tutte le marche"
            testId="filter-brand"
            emptyText="Nessuna marca"
          />
        </FilterField>

        <FilterField label="Tipologia" className="w-[210px] flex-none">
          <select
            value={tipo ?? ''}
            onChange={e => setTipo(e.target.value || null)}
            data-testid="filter-tipo"
            className="w-full px-2.5 py-[6px] border border-[#d8d8d4] rounded-[7px] text-[14px] bg-white focus:outline-none focus:border-[#2b3a8f]"
          >
            <option value="">Tutte</option>
            {tipoOpts.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </FilterField>

        <FilterField label="Cerca modello" className="w-[190px] flex-none">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Codice o nome modello…"
            data-testid="filter-machine-search"
            className="w-full px-2.5 py-[6px] border border-[#d8d8d4] rounded-[7px] text-[14px] focus:outline-none focus:border-[#2b3a8f]"
          />
        </FilterField>

        <label className="flex items-center gap-2 text-[14px] text-[#5c5c57] cursor-pointer flex-none">
          <input type="checkbox" checked={unmatched} onChange={e => setUnmatched(e.target.checked)} data-testid="filter-unmatched" />
          Solo senza associazioni
        </label>
      </div>

      <div className="flex items-center justify-between gap-3 mb-3.5 flex-none">
        <p className="text-[14px] font-mono text-[#5c5c57]">
          {total.toLocaleString('it')} risultati · {rows.length} caricate
          {selected.size > 0 && (
            <>
              {' · '}
              <span className="text-[#2b3a8f] font-semibold">{selected.size} selezionate</span>
            </>
          )}
        </p>

        {/* Swap-area: nessun bump perché entrambi gli stati hanno la stessa altezza */}
        {selected.size > 0 ? (
          <div
            className="flex items-center gap-2 px-3 py-1.5 bg-[#eef1fb] border border-[#d4dcf7] rounded-[9px] flex-none"
            data-testid="bulk-bar"
          >
            <button
              onClick={() => setSelected(new Set())}
              className="text-[13px] text-[#5c6bb0] hover:underline px-1"
              data-testid="bulk-deselect"
            >
              deseleziona
            </button>
            <div className="w-px h-4 bg-[#d4dcf7]" />
            <button className="text-[14px] font-semibold text-[#2b3a8f] bg-white border border-[#c3cdf2] px-2.5 py-1.5 rounded-[7px] hover:bg-[#fafbff]">Abbina articoli →</button>
            <button className="text-[14px] font-semibold text-[#5c5c57] bg-white border border-[#cfcfca] px-2.5 py-1.5 rounded-[7px] hover:bg-[#fafafa]">Copia associazioni</button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-none">
            <button
              onClick={() => setCreateOpen(true)}
              data-testid="add-machine-btn"
              className="px-3.5 py-2 rounded-lg bg-[#2b3a8f] text-white text-[14px] font-semibold hover:bg-[#243279] flex items-center gap-1.5 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Nuova macchina
            </button>
            <button className="px-3.5 py-2 border border-[#cfcfca] rounded-lg bg-white text-[14px] font-semibold text-[#3a3a36] hover:bg-[#fafafa] flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Esporta CSV
            </button>
          </div>
        )}
      </div>

      {/* Table (header sticky + virtual list via infinite scroll) */}
      <div className="bg-white border border-[#e2e2df] rounded-[11px] overflow-hidden flex flex-col flex-1 min-h-0">
        <div className="grid grid-cols-[34px_1.7fr_1fr_1fr_80px_140px_90px] bg-[#fafafa] border-b border-[#e4e4e1] flex-none">
          <div className="py-2.5 px-3 text-center"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all-machines" /></div>
          <div className="py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">MODELLO</div>
          <div className="py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">MARCA</div>
          <div className="py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">TIPOLOGIA</div>
          <div className="py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono text-right">COMPAT.</div>
          <div className="py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">RELAZIONI</div>
          <div className="py-2.5 px-3 text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">AZIONI</div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="machines-scroll">
          {rows.length === 0 && !loading ? (
            <div className="px-4 py-16 text-center text-[14px] text-[#5c5c57]">Nessuna macchina trovata</div>
          ) : (
            <>
              {rows.map(m => {
                const sel = selected.has(m.id);
                return (
                  <div
                    key={m.id}
                    data-testid={`machine-row-${m.id}`}
                    style={{ height: ROW_HEIGHT }}
                    className={`grid grid-cols-[34px_1.7fr_1fr_1fr_80px_140px_90px] border-b border-[#f1f1ee] cursor-pointer items-center transition-colors ${
                      sel ? 'bg-[#fafbff]' : 'hover:bg-[#fafafa]'
                    }`}
                    onClick={() => toggleOne(m.id)}
                  >
                    <div className="px-3 text-center">
                      <input type="checkbox" checked={sel} onChange={() => toggleOne(m.id)} onClick={e => e.stopPropagation()} />
                    </div>
                    <div className="px-3 text-[15px] font-semibold text-[#1f1f1d] truncate">{m.model}</div>
                    <div className="px-3 text-[15px] text-[#5c5c57] truncate">{m.brand}</div>
                    <div className="px-3 text-[14px] text-[#5c5c57]">{tutteTipologie.find(t => t.code === m.type)?.label ?? m.type ?? '—'}</div>
                    <div className="px-3 text-[15px] font-semibold text-[#3a3a36] text-right">{m.compat}</div>
                    <div className="px-3">
                      {m.rebrandFrom ? (
                        <span className="text-[12px] font-bold font-mono bg-purple-100 text-purple-700 px-2 py-1 rounded">Rebrand</span>
                      ) : null}
                    </div>
                    <div className="px-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openDialog(m)}
                        data-testid={`vedi-machine-${m.id}`}
                        className="text-[14px] font-semibold text-[#2b3a8f] hover:underline flex items-center gap-1"
                        title="Visualizza articoli associati"
                      >
                        <Link2 className="w-3 h-3" /> vedi
                      </button>
                    </div>
                  </div>
                );
              })}
              <div ref={sentinelRef} className="h-8 flex items-center justify-center text-[13px] font-mono text-[#5c5c57]">
                {loading
                  ? 'Caricamento…'
                  : hasMore
                  ? '↓ scorri per caricare altri'
                  : rows.length > 0 ? `fine elenco · ${rows.length} risultati` : ''}
              </div>
            </>
          )}
        </div>
      </div>

      {dialogMachine && (
        <MachineDetailModal
          machine={dialogMachine}
          onClose={() => setDialogMachine(null)}
          marche={tutteMarche}
          tipologie={tutteTipologie}
          onMachineUpdated={refreshList}
        />
      )}

      {createOpen && (
        <CreateMachineModal
          onClose={() => setCreateOpen(false)}
          marche={tutteMarche}
          tipologie={tutteTipologie}
          onCreated={refreshList}
        />
      )}
    </div>
  );
}
